import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const CartDrawer = () => {
  const { cartItems, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-[#1A1513]/50 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#1A1513] z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#362B25]">
          <div className="flex items-center space-x-2">
            <ShoppingBag className="h-6 w-6 text-[#D4A373]" />
            <h2 className="text-xl font-bold">Your Cart</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCartOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="h-16 w-16 text-[#B5A79E] mx-auto mb-4" />
              <p className="text-[#B5A79E] mb-2">Your cart is empty</p>
              <Button onClick={() => setIsCartOpen(false)} variant="outline">
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => (
                <div key={item.variant ? `${item.id}-${item.variant}` : item.id} className="flex space-x-4 p-4 bg-[#0C0A09] rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#F7F3EE]">{item.name}</h3>
                    <p className="text-sm text-[#B5A79E] mb-1">{item.category}</p>
                    {item.variantLabel && (
                      <p className="text-xs mb-2" style={{ color: '#D4A373' }}>
                        {item.variantLabel}
                      </p>
                    )}
                    <p className="text-lg font-bold text-[#D4A373]">${item.price}</p>
                  </div>
                  
                  <div className="flex flex-col justify-between items-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeFromCart(item.id, item.variant)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex items-center space-x-2 bg-[#1A1513] border border-[#362B25] rounded-lg">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.id, item.quantity - 1, item.variant)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.id, item.quantity + 1, item.variant)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-[#362B25] p-6 space-y-4">
            {/* Tiered Savings Nudge */}
            {(() => {
              const total = getCartTotal();
              const STARTER_THRESHOLD = 100;
              const SHIPPING_THRESHOLD = 150;
              const starterUnlocked = total >= STARTER_THRESHOLD;
              const shippingUnlocked = total >= SHIPPING_THRESHOLD;

              // Determine which milestone to surface in the headline.
              let headline;
              if (!starterUnlocked) {
                const left = (STARTER_THRESHOLD - total).toFixed(2);
                headline = (
                  <>
                    Add <span className="font-bold" style={{ color: '#D4A373' }}>${left}</span> more for the <span className="font-bold" style={{ color: '#D4A373' }}>FREE Starter Pack</span>
                  </>
                );
              } else if (!shippingUnlocked) {
                const left = (SHIPPING_THRESHOLD - total).toFixed(2);
                headline = (
                  <>
                    Starter Pack unlocked! Add <span className="font-bold" style={{ color: '#D4A373' }}>${left}</span> more for <span className="font-bold" style={{ color: '#D4A373' }}>FREE Shipping</span>
                  </>
                );
              } else {
                headline = (
                  <span className="font-bold" style={{ color: '#86EFAC' }}>
                    ✓ FREE Shipping &amp; FREE Starter Pack unlocked
                  </span>
                );
              }

              const pct = Math.min(100, (total / SHIPPING_THRESHOLD) * 100);

              return (
                <div
                  data-testid="tier-nudge"
                  className="rounded-md p-3"
                  style={{
                    backgroundColor: '#0C0A09',
                    border: '1px dashed rgba(212, 163, 115, 0.35)',
                  }}
                >
                  <p className="text-xs sm:text-sm mb-2" style={{ color: '#F7F3EE' }}>
                    {headline}
                  </p>
                  <div
                    className="relative w-full h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: '#1A1513' }}
                  >
                    <div
                      className="absolute inset-y-0 left-0 transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        background:
                          'linear-gradient(90deg, #D4A373 0%, #F5E6D3 50%, #D4A373 100%)',
                      }}
                    />
                    {/* Starter milestone marker */}
                    <div
                      className="absolute top-0 bottom-0"
                      style={{
                        left: `${(STARTER_THRESHOLD / SHIPPING_THRESHOLD) * 100}%`,
                        width: '2px',
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] mt-1.5 font-bold uppercase tracking-wider">
                    <span style={{ color: starterUnlocked ? '#86EFAC' : '#5A4A3F' }}>
                      {starterUnlocked ? '✓' : ''} $100 Starter Pack
                    </span>
                    <span style={{ color: shippingUnlocked ? '#86EFAC' : '#5A4A3F' }}>
                      {shippingUnlocked ? '✓' : ''} $150 Free Ship
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center justify-between text-lg">
              <span className="font-semibold">Subtotal:</span>
              <span className="text-2xl font-bold text-[#D4A373]">${getCartTotal().toFixed(2)}</span>
            </div>
            <Button 
              className="w-full bg-[#D4A373] hover:bg-[#E6CC98] text-white h-12 text-lg"
              onClick={handleCheckout}
            >
              Proceed to Checkout
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setIsCartOpen(false)}
            >
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
